import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TechnicalSpecifications = ({ asset, isEditing, onUpdate }) => {
  const osOptions = [
    { value: 'windows11', label: 'Windows 11' },
    { value: 'windows10', label: 'Windows 10' },
    { value: 'macos', label: 'macOS' },
    { value: 'ubuntu', label: 'Ubuntu Linux' },
    { value: 'centos', label: 'CentOS' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
    { value: 'other', label: 'Other' }
  ];

  const ramOptions = [
    { value: '4gb', label: '4 GB' },
    { value: '8gb', label: '8 GB' },
    { value: '16gb', label: '16 GB' },
    { value: '32gb', label: '32 GB' },
    { value: '64gb', label: '64 GB' },
    { value: '128gb', label: '128 GB' }
  ];

  const storageOptions = [
    { value: '256gb', label: '256 GB' },
    { value: '512gb', label: '512 GB' },
    { value: '1tb', label: '1 TB' },
    { value: '2tb', label: '2 TB' },
    { value: '4tb', label: '4 TB' },
    { value: '8tb', label: '8 TB' }
  ];

  const handleFieldChange = (field, value) => {
    const updatedSpecs = { ...asset?.technicalSpecs, [field]: value };
    onUpdate({ ...asset, technicalSpecs: updatedSpecs });
  };

  const specs = asset?.technicalSpecs || {};

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <h3 className="text-lg font-semibold text-foreground mb-6">Technical Specifications</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <Input
            label="Processor/CPU"
            type="text"
            value={specs?.processor || ''}
            onChange={(e) => handleFieldChange('processor', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Intel Core i7-12700H"
          />

          <Select
            label="RAM/Memory"
            options={ramOptions}
            value={specs?.ram || ''}
            onChange={(value) => handleFieldChange('ram', value)}
            disabled={!isEditing}
            placeholder="Select RAM capacity"
          />

          <Select
            label="Storage Capacity"
            options={storageOptions}
            value={specs?.storage || ''}
            onChange={(value) => handleFieldChange('storage', value)}
            disabled={!isEditing}
            placeholder="Select storage capacity"
          />

          <Input
            label="Graphics Card"
            type="text"
            value={specs?.graphics || ''}
            onChange={(e) => handleFieldChange('graphics', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., NVIDIA RTX 4060"
          />

          <Input
            label="Display Size"
            type="text"
            value={specs?.displaySize || ''}
            onChange={(e) => handleFieldChange('displaySize', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 15.6 inches"
          />

          <Input
            label="Resolution"
            type="text"
            value={specs?.resolution || ''}
            onChange={(e) => handleFieldChange('resolution', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 1920x1080"
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <Select
            label="Operating System"
            options={osOptions}
            value={specs?.operatingSystem || ''}
            onChange={(value) => handleFieldChange('operatingSystem', value)}
            disabled={!isEditing}
            placeholder="Select operating system"
          />

          <Input
            label="OS Version"
            type="text"
            value={specs?.osVersion || ''}
            onChange={(e) => handleFieldChange('osVersion', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 22H2"
          />

          <Input
            label="Network Connectivity"
            type="text"
            value={specs?.networkConnectivity || ''}
            onChange={(e) => handleFieldChange('networkConnectivity', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Wi-Fi 6, Ethernet, Bluetooth 5.2"
          />

          <Input
            label="Ports & Interfaces"
            type="text"
            value={specs?.ports || ''}
            onChange={(e) => handleFieldChange('ports', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 2x USB-A, 2x USB-C, HDMI"
          />

          <Input
            label="Power Requirements"
            type="text"
            value={specs?.powerRequirements || ''}
            onChange={(e) => handleFieldChange('powerRequirements', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 65W adapter, 120V-240V"
          />

          <Input
            label="Dimensions (L x W x H)"
            type="text"
            value={specs?.dimensions || ''}
            onChange={(e) => handleFieldChange('dimensions', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., 35.7 x 23.5 x 1.8 cm"
          />
        </div>
      </div>
      {/* Software & Licenses */}
      <div className="mt-6">
        <h4 className="text-md font-medium text-foreground mb-4">Installed Software & Licenses</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Input
            label="Antivirus Software"
            type="text"
            value={specs?.antivirus || ''}
            onChange={(e) => handleFieldChange('antivirus', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Windows Defender, Norton"
          />

          <Input
            label="Office Suite"
            type="text"
            value={specs?.officeSuite || ''}
            onChange={(e) => handleFieldChange('officeSuite', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Microsoft Office 365"
          />

          <Input
            label="Development Tools"
            type="text"
            value={specs?.developmentTools || ''}
            onChange={(e) => handleFieldChange('developmentTools', e?.target?.value)}
            disabled={!isEditing}
            placeholder="e.g., Visual Studio Code, Git"
          />

          <Input
            label="Other Software"
            type="text"
            value={specs?.otherSoftware || ''}
            onChange={(e) => handleFieldChange('otherSoftware', e?.target?.value)}
            disabled={!isEditing}
            placeholder="List other installed software"
          />
        </div>
      </div>
      {/* Additional Technical Notes */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Technical Notes
        </label>
        <textarea
          value={specs?.technicalNotes || ''}
          onChange={(e) => handleFieldChange('technicalNotes', e?.target?.value)}
          disabled={!isEditing}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder="Enter any additional technical information..."
        />
      </div>
    </div>
  );
};

export default TechnicalSpecifications;